import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  observeOn,
  of,
  queueScheduler,
  switchMap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  CanvasElementPath,
  CanvasElementPathNode,
  CanvasElementType,
  CanvasNamespaceId,
  getRootElementId,
  RendererNodeItem,
  ROOT_ELEMENT_INNER_ID,
  SlotData,
  SlotIdOf,
  SlotNodeData,
  Slots,
} from '@blaze-runtime/engine-domain';
import {
  ChangePending,
  ElementMetaData,
  RenderElementContext,
} from '@blaze-shared/elements-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { State } from '../+state';
import { getFirstOverlayHostedNs, getSelectedPathNode, getSelectionData } from '../+state/elements';
import { hasSlots } from '../canvas-elements/element-model/element-props-guards';
import { isContainerType } from '../canvas-elements/element-model/element-type-guards';
import { ElementDefinitionsService } from '../services/element-definitions.service';
import { LayoutLockService } from '../services/layout-lock.service';
import { isOnPath, isSelected } from '../utils/canvas-element/path';
import { getElementSizeOverride } from '../utils/elements/element.utils';

import {
  AddRelatedContext,
  ColumnAddHandleContext,
  ColumnDividerContext,
  ColumnResizeHandleContext,
  ElementWrapperContext,
  LoadingIndicatorContext,
  SelectionOverlayContext,
  SelectionToolbarContext,
  WorkflowErrorsContext,
} from './node-renderer.template-contexts';

interface NodeRendererModel {
  ids: CanvasElementId[];
  elements: Dictionary<CanvasElement>;
  metadata: Dictionary<ElementMetaData>;
  elementContext: RenderElementContext | undefined;
}

@Component({
  selector: 'blaze-nr',
  templateUrl: './node-renderer.component.html',
  styleUrls: ['./node-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NodeRendererComponent implements OnChanges {
  private readonly store = inject(Store<State>);
  private readonly layoutLockService = inject(LayoutLockService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);

  @Input() bypassLazyRendering = false;
  @Input() ids?: CanvasElementId[] | null;
  @Input() elements?: Dictionary<CanvasElement> | null;
  @Input() metadata?: Dictionary<ElementMetaData> | null;
  @Input() elementContext: RenderElementContext | undefined;

  @Input() selectionOverlayTpl: TemplateRef<SelectionOverlayContext> | null = null;
  @Input() columnAddHandleTpl: TemplateRef<ColumnAddHandleContext> | null = null;
  @Input() columnDividerTpl: TemplateRef<ColumnDividerContext> | null = null;
  @Input() columnResizeHandleTpl: TemplateRef<ColumnResizeHandleContext> | null = null;
  @Input() elementWrapperTpl: TemplateRef<ElementWrapperContext> | null = null;
  @Input() loadingIndicatorTpl: TemplateRef<LoadingIndicatorContext> | null = null;
  @Input() selectionToolbarTpl: TemplateRef<SelectionToolbarContext> | null = null;
  @Input() workflowErrorsTpl: TemplateRef<WorkflowErrorsContext> | null = null;
  @Input() addRelatedButtonTpl: TemplateRef<AddRelatedContext> | null = null;

  private readonly model$$ = new BehaviorSubject<NodeRendererModel>({
    ids: [],
    elements: {},
    elementContext: undefined,
    metadata: {},
  });

  private readonly nodeRenderedId = crypto.randomUUID();
  private layoutLockIndex = 0;

  readonly selectedNodePath$ = this.store.select(getSelectedPathNode);
  readonly pendingUi$ = this.store.select(runtimeSelectors.selectPendingUi);
  readonly overlayNs$ = this.store.select(getFirstOverlayHostedNs);

  readonly model$ = this.model$$.asObservable();
  readonly items$ = this.getItems();

  @HostBinding('class.force-expand') get shouldForceExpand(): boolean {
    return this.model$$.value?.elementContext?.forceExpandElements ?? false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const modelChanges: Partial<NodeRendererModel> = {};

    // Deep equality needed - the value is created on the fly
    if (changes.ids && !isDeepEqual(changes.ids.previousValue, changes.ids.currentValue)) {
      modelChanges.ids = changes.ids.currentValue || [];
    }

    // It's a reference to immutable state - no need to deep compare
    if (changes.elements && changes.elements.previousValue !== changes.elements.currentValue) {
      modelChanges.elements = changes.elements.currentValue || {};
    }

    // Deep equality needed - the value is merged on the fly
    if (
      changes.elementContext &&
      !isDeepEqual(changes.elementContext.previousValue, changes.elementContext.currentValue)
    ) {
      modelChanges.elementContext = changes.elementContext.currentValue;
    }

    // It's a reference to immutable state - no need to deep compare
    if (changes.metadata && changes.metadata.previousValue !== changes.metadata.currentValue) {
      modelChanges.metadata = changes.metadata.currentValue;
    }

    if (Object.keys(modelChanges).length > 0) {
      this.model$$.next({
        ...this.model$$.value,
        ...modelChanges,
      });
    }
  }

  combineContexts(
    inheritedContext: RenderElementContext | undefined,
    itemContext: RenderElementContext | undefined,
  ): RenderElementContext | undefined {
    if (!inheritedContext && !itemContext) {
      return undefined;
    }

    return { ...inheritedContext, ...itemContext };
  }

  // expose the function to the view
  getChildrenIds(element: CanvasElement, slotId: string): CanvasElementId[] {
    if (hasSlots(element)) {
      const slot = element.slots[slotId];
      if (slot?.hosting?.ns) {
        // If slot hosts a nested layout - point to the root of this layout.
        return [getRootElementId(slot.hosting.ns)];
      } else {
        return slot?.children || [];
      }
    } else {
      return [];
    }
  }

  isLoading(item: RendererNodeItem, pending: Record<CanvasElementId, ChangePending[]>): boolean {
    return (
      item.element !== undefined &&
      this.elementDefinitionsService.getFeature(item.element.type, 'loadingIndication') !==
        undefined &&
      pending[item.element.id]?.some((changePending) => changePending.cause !== 'row-action')
    );
  }

  isLoadingWithIndicator(
    item: RendererNodeItem,
    pending: Record<CanvasElementId, ChangePending[]>,
    overlayNs: CanvasNamespaceId | undefined,
  ): boolean {
    return (
      this.isLoading(item, pending) &&
      this.elementDefinitionsService.getFeature(item.element.type, 'loadingIndication') ===
        'fadeWithIndicator' &&
      (!overlayNs || overlayNs === item.element.ns)
    );
  }

  /**
   * Returns the items to render in this node.
   */
  private getItems(): Observable<RendererNodeItem[]> {
    return combineLatest({
      model: this.model$$,
      selection: this.store.select(getSelectionData),
    }).pipe(
      switchMap(({ model, selection }) => {
        // Prepare lock id and set the lock
        const lockId = this.setLock();
        // Get the scheduler for the current node rendering
        const scheduler = this.getSchedulerForNode(model);

        return of({ model, selection }).pipe(
          // For performance reasons, we want to render the nodes on the next animation frame
          observeOn(scheduler),
          // Map the data to items with render data (slots, selection data, etc.)
          map(({ model, selection }) => getRendererNodeItems(model, selection)),
          // Remove the lock after `of` emits and completes making sure changes are propagated.
          finalize(() => this.removeLock(lockId)),
        );
      }),
      distinctUntilChanged(isDeepEqual),
    );
  }

  /**
   * Returns the scheduler for the current node rendering.
   * The scheduler is used to delay (or not) the rendering of nested layouts for better user experience.
   * Nav menus should be rendered as quickly as possible to avoid layout shifts
   * The content might be delayed to the next animation frame to avoid blocking the main thread for too long
   * to give the user a feeling of a shorter loading time.
   * @param model The model of the node to render
   * @returns The scheduler to use
   */
  private getSchedulerForNode(
    model: NodeRendererModel,
  ): typeof queueScheduler | typeof animationFrameScheduler {
    if (this.bypassLazyRendering) {
      return queueScheduler; // Load instantly
    }

    // Header and nav menus should not be delayed for better user experience
    const isRoot = model.ids.length === 1 && model.ids[0].endsWith(`_${ROOT_ELEMENT_INNER_ID}`);
    // Load instantly if root or nav section/dialog/data container(form-like)
    const ec = model.elementContext;
    const shouldBeLoadedInstantly =
      isRoot ||
      ec?.navSection !== undefined ||
      ec?.viewType === 'dialog' ||
      ec?.dataContainer !== undefined;

    return shouldBeLoadedInstantly
      ? queueScheduler // Load instantly (less layout shifts)
      : animationFrameScheduler; // Load on the next animation frame (better user experience for nested layouts)
  }

  /**
   * Sets a lock for the current layout rendering.
   * @returns The lock id
   */
  private setLock(): string {
    const lockId = `${this.nodeRenderedId}_${this.layoutLockIndex}`;
    this.layoutLockIndex++;
    this.layoutLockService.setLock(lockId);
    return lockId;
  }

  /**
   * Removes a lock for the current layout rendering.
   * @param lockId The lock id to remove
   */
  private removeLock(lockId: string): void {
    // The lock is removed after the propagating to the following level (setTimeout) to make sure the change got propagated
    // and the following step (nesting level) started showing up and created its own lock
    setTimeout(() => this.layoutLockService.removeLock(lockId));
  }
}

function getRendererNodeItems(
  model: NodeRendererModel,
  selection: {
    pathNode: CanvasElementPathNode | undefined;
    path: CanvasElementPath;
  },
): RendererNodeItem[] {
  const renderItems = model.ids.map<RendererNodeItem | undefined>((id, index) => {
    const element = model.elements[id];
    if (!element) {
      console.warn(`Element with id ${id} not found in the state`);
      return undefined;
    }

    return {
      element,
      slotNodes: getSlotNodes(id, selection.path, element?.type, element?.slots),
      isSelected:
        selection.pathNode?.id === id &&
        selection.pathNode?.slot === undefined &&
        (selection.pathNode?.rowContext?.rowId === undefined ||
          model.elementContext?.rowContext?.rowId === undefined ||
          selection.pathNode?.rowContext?.rowId === model.elementContext?.rowContext?.rowId),
      isOnPath: isOnPath(selection.path, id),
      index,
      elementContext: model.elementContext,
      features: model.metadata[id]?.renderFeatures,
      sizeOverride: getElementSizeOverride(element, model.elements),
    };
  });

  // Filter out items which could not be found in the state
  return renderItems.filter((item): item is RendererNodeItem => item !== undefined);
}

function getSlotNodes<T extends CanvasElementType>(
  elementId: CanvasElementId,
  selectedPath: CanvasElementPath,
  elementType?: CanvasElementType,
  slots?: Slots<SlotIdOf<T>>,
): SlotNodeData[] {
  if (!slots) {
    return [];
  }

  return slots.ids.map<SlotNodeData>((slotId) => ({
    slotId,
    elementId,
    isSelected: isSelected(selectedPath, elementId, slotId),
    isOnPath: isOnPath(selectedPath, elementId, slotId),
    // Only container has dynamic columns
    isColumn: isContainerType(elementType),
    hosting: (slots[slotId] as SlotData)?.hosting,
    sizing: (slots[slotId] as SlotData)?.sizing,
    alignment: (slots[slotId] as SlotData)?.alignment,
    props: (slots[slotId] as SlotData)?.props,
  }));
}
